import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

/*
 * Klub va to'garaklar — Renessans Academic Clubs (RAC).
 * public/rac/index.html fetch bilan olinib, iframe'ga srcdoc sifatida qo'yiladi.
 * Shuning uchun serverdagi "X-Frame-Options: DENY" sarlavhasi to'sqinlik qilmaydi
 * (srcdoc uchun HTTP javob yo'q). API'lar: /api/rac/ (Django -> clubs.rtu.uz).
 *
 * RAC ichki sahifasi manzilda ?p=/club/<id> bo'lib saqlanadi: havolani ulashish,
 * sahifani yangilash va brauzerning "Orqaga" tugmasi ishlaydi.
 *
 * Iframe balandligi RAC kontentiga moslanadi ({rac:'height'} xabari) — sahifada
 * bitta scroll bo'ladi. Sayt ichida faqat katalog ko'rinadi, shuning uchun
 * tahrirlovchi/eksport kutubxonalari (Quill, Chart.js, XLSX, jsPDF) yuklanmaydi.
 */
const RAC_SRC = "/rac/index.html";
const DEFAULT_PATH = "/catalog";

const cleanPath = (p) => {
  const s = String(p || "").trim();
  return s.startsWith("/") && !s.startsWith("//") ? s : DEFAULT_PATH;
};

const RacClubs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const frameRef = useRef(null);
  const lastPath = useRef(cleanPath(searchParams.get("p")));
  const [html, setHtml] = useState(null);
  const [error, setError] = useState(false);
  const [height, setHeight] = useState(900);

  // RAC sahifasini bir marta yuklab olish
  useEffect(() => {
    let alive = true;
    fetch(RAC_SRC, { cache: "no-cache" })
      .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
      .then((text) => {
        if (!text.includes("Renessans Academic Clubs")) throw new Error("rac/index.html topilmadi");
        const start = JSON.stringify(lastPath.current).replace(/</g, "\\u003c");
        const inject = `<base href="${window.location.origin}/"><script>window.__RAC_START=${start};</script>`;
        const doc = text
          .replace(/<script src="https:\/\/cdnjs\.cloudflare\.com\/ajax\/libs\/(quill|Chart\.js|xlsx|jspdf|jspdf-autotable)\/[^"]*"><\/script>\s*/g, "")
          .replace(/<link rel="stylesheet" href="https:\/\/cdnjs\.cloudflare\.com\/ajax\/libs\/quill\/[^"]*">\s*/g, "")
          .replace(/<head>/i, `<head>${inject}`);
        if (alive) setHtml(doc);
      })
      .catch(() => alive && setError(true));
    return () => { alive = false; };
  }, []);

  // Sahifaning qaysi qismi ko'rinib turganini RAC'ga yuborish — to'garak
  // muqovalari faqat ekranga yaqinlashganda yuklanadi (tezlik uchun)
  useEffect(() => {
    let raf = 0;
    const send = () => {
      raf = 0;
      const el = frameRef.current;
      if (!el || !el.contentWindow) return;
      const r = el.getBoundingClientRect();
      el.contentWindow.postMessage({ rac: "view", top: -r.top, bottom: window.innerHeight - r.top }, "*");
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(send); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [html, height]);

  // RAC ichida sahifa almashdi -> manzilga yozish (tarixga yangi qadam)
  useEffect(() => {
    const onMsg = (e) => {
      if (!frameRef.current || e.source !== frameRef.current.contentWindow) return;
      const d = e.data || {};
      if (d.rac === "height") {
        const h = Number(d.h);
        if (h > 0) setHeight(Math.ceil(h));
        return;
      }
      if (d.rac !== "route") return;
      const p = cleanPath(d.path);
      if (p === lastPath.current) return;
      lastPath.current = p;
      setSearchParams(p === DEFAULT_PATH ? {} : { p });
      scrollToFrame();
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [setSearchParams]);

  // Brauzerda "Orqaga"/"Oldinga" bosildi -> RAC'ga shu sahifani ochishni aytish
  const urlPath = cleanPath(searchParams.get("p"));
  useEffect(() => {
    if (urlPath === lastPath.current) return;
    lastPath.current = urlPath;
    const w = frameRef.current && frameRef.current.contentWindow;
    if (w) w.postMessage({ rac: "go", path: urlPath }, "*");
    scrollToFrame();
  }, [urlPath]);

  function scrollToFrame() {
    const el = frameRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 90;
    if (window.scrollY > top) window.scrollTo({ top: Math.max(0, top) });
  }

  if (error) {
    return (
      <div style={{ maxWidth: 560, margin: "48px auto", padding: 24, textAlign: "center" }}>
        <h3>Klub va to'garaklar sahifasi yuklanmadi</h3>
        <p>Serverda <code>/rac/index.html</code> topilmadi yoki ochilmadi. Birozdan so'ng qayta urining.</p>
      </div>
    );
  }

  return (
    <div style={{ width: "100%", background: "#F3F5F8", minHeight: html ? 0 : 600 }}>
      {html && (
        <iframe
          ref={frameRef}
          srcDoc={html}
          title="Klub va to'garaklar — Renessans Academic Clubs"
          scrolling="no"
          style={{ display: "block", width: "100%", height, border: 0, overflow: "hidden" }}
          allow="clipboard-write"
        />
      )}
    </div>
  );
};

export default RacClubs;
